import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Instagram, Facebook, Menu, X, ShoppingBag, Phone, User, ChevronDown } from 'lucide-react';
import useCartStore from '../store/cartStore';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'treatments' | 'about' | null
  const [mobileSubOpen, setMobileSubOpen] = useState({ treatments: false, about: false });
  const { items, openCart } = useCartStore();
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  // Top-level nav with optional nested `children`. Dropdowns show on hover
  // (desktop) and expand inline (mobile). Keeps pillar pages one click deep.
  const navItems = [
    { label: 'Home', path: '/' },
    {
      label: 'Treatments',
      key: 'treatments',
      path: '/facials',
      children: [
        { label: 'All Facials', path: '/facials', desc: 'Full treatment menu' },
        { label: 'Microneedling & CIT', path: '/treatments/microneedling', desc: 'Collagen induction therapy' },
        { label: 'Acne Treatment', path: '/treatments/acne', desc: 'Adult acne + hormonal breakouts' },
        { label: 'Skin Quiz', path: '/skin-quiz', desc: 'Find your ideal treatment' },
      ],
    },
    { label: 'Shop', path: '/shop' },
    { label: 'Membership', path: '/membership' },
    { label: 'Gift Cards', path: '/gift-cards' },
    { label: 'Blog', path: '/blog' },
    {
      label: 'About',
      key: 'about',
      path: '/about',
      children: [
        { label: 'About the Studio', path: '/about', desc: 'Meet Biana + our approach' },
        { label: 'Our Story', path: '/our-story', desc: 'Why we opened AT THE BEAUTY BAR' },
        { label: 'Testimonials', path: '/testimonials', desc: 'Real client reviews' },
        { label: 'Areas We Service', path: '/areas-we-service', desc: 'Local Melbourne suburbs' },
        { label: 'FAQ', path: '/faq', desc: 'Common questions answered' },
      ],
    },
    { label: 'Contact', path: '/contact' },
  ];

  const isActive = (item) => {
    if (item.path === location.pathname) return true;
    if (item.children) return item.children.some((c) => c.path === location.pathname);
    return false;
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMobileSub = (key) => {
    setMobileSubOpen((s) => ({ ...s, [key]: !s[key] }));
  };

  return (
    <>
      {/* Top announcement strip — free shipping CTA + studio line on desktop */}
      <div
        className="bg-beauty-black text-white tracking-[0.15em] uppercase"
        data-testid="top-announcement-bar"
      >
        <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3 text-[11px] sm:text-xs">
          <span className="flex-1 text-center md:text-left">
            <span className="opacity-80">Free Australia-wide delivery on Murad orders over</span>{' '}
            <span className="text-beauty-green-300 font-semibold">$150</span>
            <span className="hidden sm:inline opacity-60"> · Gift cards sent instantly</span>
          </span>
          <a
            href="tel:1300456987"
            className="hidden md:inline-flex items-center gap-1.5 text-beauty-green-300 hover:text-white transition-colors font-semibold"
            data-testid="top-studio-line"
            aria-label="Call our studio line on 1300 GLOW UP"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden="true" />
            <span className="text-white/70 font-normal normal-case tracking-normal">Studio Line</span>
            <span>1300 GLOW UP</span>
          </a>
        </div>
      </div>

      <header className="beauty-glass sticky top-0 z-50 border-b border-white/20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {/* Logo - sized for clean alignment with nav */}
            <div className="cursor-pointer flex-shrink-0 max-w-[55%] lg:max-w-none" onClick={() => navigate('/')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_8cdfec3d-bf66-4921-8f38-fb134be3ab3c/artifacts/b4t8nbux_MagicEraser_260124_224947.png" 
                alt="AT THE BEAUTY BAR" 
                className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto"
              />
            </div>

            {/* Desktop / Tablet Navigation Menu - All Pages + Social Media */}
            <nav className="hidden md:flex items-center gap-0.5 lg:gap-1 xl:gap-2 flex-1 justify-center min-w-0">
              {navItems.map((item) => {
                const active = isActive(item);
                if (item.children) {
                  const isOpen = openDropdown === item.key;
                  return (
                    <div
                      key={item.key}
                      className="relative"
                      onMouseEnter={() => setOpenDropdown(item.key)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <Button
                        variant="ghost"
                        className={`text-beauty-black hover:text-beauty-green hover:bg-beauty-green/10 font-medium text-[11px] md:text-xs lg:text-sm xl:text-base px-1.5 md:px-2 xl:px-3 whitespace-nowrap gap-1 ${
                          active ? 'text-beauty-green' : ''
                        }`}
                        onClick={() => navigate(item.path)}
                        aria-haspopup="true"
                        aria-expanded={isOpen}
                        data-testid={`nav-${item.key}`}
                      >
                        {item.label}
                        <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                      </Button>
                      {isOpen && (
                        <div
                          className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50 min-w-[280px]"
                          role="menu"
                          data-testid={`nav-${item.key}-dropdown`}
                        >
                          <div className="bg-white border border-black/5 rounded-lg shadow-xl py-2 overflow-hidden">
                            {item.children.map((child) => (
                              <button
                                key={child.path}
                                onClick={() => {
                                  setOpenDropdown(null);
                                  navigate(child.path);
                                }}
                                className={`w-full text-left px-4 py-3 hover:bg-beauty-green/5 transition-colors group ${
                                  location.pathname === child.path ? 'bg-beauty-green/5' : ''
                                }`}
                                role="menuitem"
                                data-testid={`nav-${item.key}-${child.path.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                              >
                                <span className={`block text-sm font-medium ${
                                  location.pathname === child.path ? 'text-beauty-green' : 'text-beauty-black group-hover:text-beauty-green'
                                }`}>
                                  {child.label}
                                </span>
                                {child.desc && (
                                  <span className="block text-xs text-beauty-text/75 mt-0.5">{child.desc}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Button
                    key={item.path}
                    variant="ghost"
                    className={`text-beauty-black hover:text-beauty-green hover:bg-beauty-green/10 font-medium text-[11px] md:text-xs lg:text-sm xl:text-base px-1.5 md:px-2 xl:px-3 whitespace-nowrap ${
                      active ? 'text-beauty-green' : ''
                    }`}
                    onClick={() => navigate(item.path)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </nav>

            {/* Book Now + Cart - Desktop / Tablet */}
            <div className="hidden md:flex flex-shrink-0 items-center gap-1.5 lg:gap-2 xl:gap-4">
              <button
                onClick={() => navigate('/my-account')}
                className="p-2 hover:bg-beauty-green/10 rounded-lg transition-colors"
                data-testid="header-account-btn"
                aria-label="My account"
              >
                <User className="w-5 h-5 text-beauty-black" aria-hidden="true" />
              </button>
              <button
                onClick={openCart}
                className="relative p-2 hover:bg-beauty-green/10 rounded-lg transition-colors"
                data-testid="header-cart-btn"
                aria-label={totalItems > 0 ? `Open cart, ${totalItems} item${totalItems === 1 ? '' : 's'}` : 'Open cart'}
              >
                <ShoppingBag className="w-5 h-5 text-beauty-black" aria-hidden="true" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-beauty-green text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>
              <Button 
                className="beauty-button-primary text-white font-semibold px-4 xl:px-8 py-3 whitespace-nowrap"
                onClick={() => navigate('/booking')}
                data-testid="header-book-now-btn"
              >
                BOOK NOW
              </Button>
            </div>

            {/* Mobile Actions (phones only) */}
            <div className="flex items-center gap-1 md:hidden flex-shrink-0">
              <a
                href="tel:1300456987"
                className="p-1.5 hover:bg-beauty-green/10 rounded-lg transition-colors"
                aria-label="Call studio line"
                data-testid="mobile-phone-btn"
              >
                <Phone className="w-5 h-5 text-beauty-green" strokeWidth={2} />
              </a>
              <button
                onClick={openCart}
                className="relative p-1.5 hover:bg-beauty-green/10 rounded-lg transition-colors"
                data-testid="mobile-cart-btn"
                aria-label={totalItems > 0 ? `Open cart, ${totalItems} item${totalItems === 1 ? '' : 's'}` : 'Open cart'}
              >
                <ShoppingBag className="w-5 h-5 text-beauty-black" aria-hidden="true" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-beauty-green text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>
              <Button 
                className="beauty-button-primary text-white font-semibold px-2.5 py-1.5 text-xs"
                onClick={() => navigate('/booking')}
                data-testid="mobile-book-now-btn"
              >
                BOOK
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                className="p-1.5 h-9 w-9"
                onClick={toggleMobileMenu}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu (phones only) */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-beauty-muted/20 py-4 relative z-40 max-h-[calc(100vh-140px)] overflow-y-auto">
          <div className="container mx-auto px-4">
            <nav className="space-y-1">
              {navItems.map((item) => {
                const active = isActive(item);
                if (item.children) {
                  const isSubOpen = mobileSubOpen[item.key];
                  return (
                    <div key={item.key} className="border-b border-beauty-muted/10 last:border-0">
                      <button
                        className={`w-full flex items-center justify-between py-3 px-2 text-left font-medium ${
                          active ? 'text-beauty-green' : 'text-beauty-black'
                        }`}
                        onClick={() => toggleMobileSub(item.key)}
                        data-testid={`mobile-nav-${item.key}`}
                      >
                        <span>{item.label}</span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${isSubOpen ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        />
                      </button>
                      {isSubOpen && (
                        <div className="pl-4 pb-2 space-y-1">
                          {item.children.map((child) => (
                            <button
                              key={child.path}
                              onClick={() => {
                                navigate(child.path);
                                setMobileMenuOpen(false);
                                setMobileSubOpen({ treatments: false, about: false });
                              }}
                              className={`block w-full text-left py-2 px-2 text-sm ${
                                location.pathname === child.path ? 'text-beauty-green font-medium' : 'text-beauty-text hover:text-beauty-green'
                              }`}
                              data-testid={`mobile-nav-${item.key}-${child.path.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                            >
                              {child.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Button
                    key={item.path}
                    variant="ghost"
                    className={`w-full justify-start text-beauty-black hover:text-beauty-green hover:bg-beauty-green/10 font-medium ${
                      active ? 'text-beauty-green' : ''
                    }`}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                  </Button>
                );
              })}

              {/* Mobile Social Media */}
              <div className="flex items-center space-x-4 pt-4 border-t border-beauty-muted/20">
                <span className="text-beauty-text text-sm">Follow us:</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-beauty-black hover:text-beauty-green hover:bg-beauty-green/10"
                  onClick={() => {
                    window.open('https://www.instagram.com/atthebeautybar/', '_blank');
                    setMobileMenuOpen(false);
                  }}
                >
                  <Instagram className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-beauty-black hover:text-beauty-green hover:bg-beauty-green/10"
                  onClick={() => {
                    window.open('https://www.facebook.com/atthebeautybar', '_blank');
                    setMobileMenuOpen(false);
                  }}
                >
                  <Facebook className="w-5 h-5" />
                </Button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;